import { useState, useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { AxiosError } from 'axios'
import { Button, Grid, TableFooter, Form, Row, InputMask } from 'components'
import { EditIcon, DeleteIcon } from 'components/Icons'
import { SuccessModal } from 'components/SuccessModal/SuccessModal'
import { useModal } from 'hooks'
import useLoading from 'hooks/useLoading'
import useToggle from 'hooks/useToggle'
import { useForm } from 'react-hook-form'
import {
  ISectorParams,
  SectorParamsServices
} from 'services/NGT/ContractsParams'
import { SPACING } from 'utils'
import * as Yup from 'yup'

import EditSectorModal from './components/EditSectorModal/EditSectorModal'
import {
  TableContainerStyled,
  TableStyled,
  TableHeadStyled,
  TableRowStyled,
  TableCellStyled,
  TableBodyStyled
} from './SectorTable.styles'

interface IForm {
  sectorName: string
}

const schema = Yup.object({
  sectorName: Yup.string().required('O campo negócio é obrigatório')
})

export const Sector = () => {
  const { showModal } = useModal()
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<IForm>({
    resolver: yupResolver(schema)
  })

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(15)
  const [listSectors, setListSectors] = useState([])
  const { loading, msgErro, setLoading, setMsgErro } = useLoading()
  const [isModalOpen, toggleModal] = useToggle()
  const [idSectorDel, setIdSectorDel] = useState<number | undefined>()
  const [selectedSector, setSelectedSector] = useState<
    ISectorParams | undefined
  >()
  const [isDeleteModalOpen, toggleDeleteModal] = useToggle()
  const [openEditSectorModal, toggleEditSectorModal] = useToggle()

  const handleChangePage = (newPage: number) => setPage(newPage)

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const getAllSectorsData = () => {
    setLoading('loading')
    SectorParamsServices()
      .getAll()
      .then(({ data }) => {
        setListSectors(data)
        setLoading('idle')
      })
      .catch((err) => {
        // setMsgErro(err.title)
        showModal({
          hideButtonCancel: true,
          message: err.title || 'Erro ao obter os dados da pesquisa.',
          txtButtonConfirm: 'OK',
          type: 'alert'
        })
        setLoading('error')
      })
  }

  const handleSelectedSector = (currentSector: number) => {
    setLoading('loading')
    SectorParamsServices()
      .get(currentSector)
      .then(({ data }) => {
        setSelectedSector(data)
        setLoading('idle')
        toggleEditSectorModal()
      })
      .catch((err) => {
        // setMsgErro(err.title)
        showModal({
          hideButtonCancel: true,
          message: err.title || 'Erro ao obter os dados da pesquisa.',
          txtButtonConfirm: 'OK',
          type: 'alert'
        })
        setLoading('error')
      })
  }

  const handleSubmitForm = ({ sectorName }) => {
    if (sectorName.trim() !== '') {
      setLoading('loading')

      SectorParamsServices()
        .create({ dsSetorContrato: sectorName })
        .then((_) => {
          setLoading('idle')
          setValue('sectorName', '')
          toggleModal()
        })
        .catch(() => {
          showModal({
            hideButtonCancel: true,
            message: 'Erro ao criar o registro, tente novamente!',
            titleModal: 'Atenção',
            txtButtonConfirm: 'Fechar',
            type: 'error'
          })
          setLoading('idle')
        })
    }
  }

  const handleEditSector = (requestData: ISectorParams) => {
    setLoading('loading')
    SectorParamsServices()
      .patch(requestData)
      .then((_) => {
        setLoading('idle')
        toggleEditSectorModal()
        getAllSectorsData()
        showModal({
          hideButtonCancel: true,
          message: 'Editado com sucesso',
          txtButtonConfirm: 'Fechar',
          type: 'success'
        })
      })
      .catch(() => {
        showModal({
          hideButtonCancel: true,
          message: 'Erro ao editar o registro, tente novamente!',
          titleModal: 'Atenção',
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
        setLoading('idle')
      })
  }

  const handleOpenDeleteModal = (idSector: number) => {
    toggleDeleteModal()
    setIdSectorDel(idSector)
  }

  const handleDeleteSector = () => {
    toggleDeleteModal()
    setLoading('loading')
    SectorParamsServices()
      .deleteSector(idSectorDel)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error(
            'Aconteceu algo inesperado ao tentar excluir o negócio.'
          )
        }

        handleChangePage(0)
        getAllSectorsData()
        showModal({
          hideButtonCancel: true,
          message: 'Excluído com sucesso',
          txtButtonConfirm: 'Fechar',
          type: 'success'
        })
      })
      .catch((error) => {
        const axiosError = error as AxiosError

        showModal({
          hideButtonCancel: true,
          message:
            axiosError.response.data?.title ||
            'Erro ao deletar, tente novamente!',
          titleModal: 'Atenção',
          txtButtonConfirm: 'Fechar',
          type: axiosError.response.data?.title ? 'alert' : 'error'
        })
      })
      .finally(() => setLoading('idle'))
  }

  useEffect(() => {
    getAllSectorsData()
  }, [])

  return (
    <>
      <Grid
        gridCols="1fr"
        gridGap={`0 ${SPACING.spacing20}`}
        margin={`${SPACING.spacing20} 0 ${SPACING.spacing30} 0`}
      >
        <Form
          onSubmit={handleSubmit(handleSubmitForm)}
          marginLeft={SPACING.spacing20}
          marginRight={SPACING.spacing20}
          marginBottom={SPACING.spacing10}
          padding={SPACING.spacing20}
        >
          <Row alignItems="center">
            <InputMask
              label="Negócio"
              control={control}
              errors={errors}
              width="400px"
              formValue="sectorName"
              placeholder="Adicionar um Negócio..."
              maxLength={20}
              type="alfanumeric"
            />
            <div style={{ width: SPACING.spacing20 }}></div>
            <Button text="Adicionar" type="submit" />
          </Row>
        </Form>
      </Grid>
      <Grid
        gridCols="5fr 3fr"
        gridGap={`0 ${SPACING.spacing10}`}
        margin={`0 0 ${SPACING.spacing30} 0`}
      >
        <TableContainerStyled>
          <TableStyled stickyHeader>
            <TableHeadStyled>
              <TableRowStyled>
                <TableCellStyled minwidth="18em">Negócios</TableCellStyled>
                <TableCellStyled>Ações</TableCellStyled>
              </TableRowStyled>
            </TableHeadStyled>
            <TableBodyStyled>
              {(rowsPerPage > 0
                ? listSectors.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                : listSectors
              ).map((row, index) => (
                <TableRowStyled
                  key={`${row}-${index}-table-body`}
                  evenNumber={index % 2 != 0}
                >
                  <TableCellStyled>{row.dsSetorContrato}</TableCellStyled>
                  <TableCellStyled>
                    <EditIcon
                      onClick={() => handleSelectedSector(row.idSetorContrato)}
                      marginRight={SPACING.spacing15}
                    />
                    <DeleteIcon
                      cursor="pointer"
                      onClick={() => handleOpenDeleteModal(row.idSetorContrato)}
                    />
                  </TableCellStyled>
                </TableRowStyled>
              ))}
            </TableBodyStyled>
          </TableStyled>
          <TableFooter
            page={page}
            rows={listSectors}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            typeFooter="offline"
          />
        </TableContainerStyled>
      </Grid>
      <SuccessModal
        open={loading === 'error'}
        message={msgErro}
        type="error"
        hideButtonCancel
        handleConfirm={() => setLoading('idle')}
        txtButtonConfirm="Voltar"
      />
      <SuccessModal
        open={isModalOpen}
        message="Negócio incluído com sucesso!"
        type="sucess"
        hideButtonCancel
        txtButtonConfirm="OK"
        handleConfirm={() => {
          toggleModal()
          getAllSectorsData()
        }}
      />
      <EditSectorModal
        showModal={openEditSectorModal}
        toggleModal={toggleEditSectorModal}
        sectorData={selectedSector}
        handleSubmitUpdate={handleEditSector}
      />
      <SuccessModal
        open={isDeleteModalOpen}
        message="Confirma exclusão (S/N)?"
        type="alert"
        txtButtonCancel="NÃO"
        txtButtonConfirm="SIM"
        handleCancel={toggleDeleteModal}
        handleConfirm={handleDeleteSector}
      />
    </>
  )
}
